import { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import ACTION from "../../store/action";
import {
  selectAssigneesAndUserGroups,
  selectProvidersDoctors,
  selectProvidersFacility,
  selectUserOrGroupById
} from "../../store/selector";
import { useGetSetting } from "../getSetting";
import useInlineEditPatient from "./useInlineEditPatient";
import useInlineEditEnrollment from "./useInlineEditEnrollment";
import { useStatuses } from "../../component/Patient/PatientApplications/hooks";
import { useInlineEditTracking } from "../../component/TableSection/InlineEdit/useInlineEditTracking";
import { buildPatientInlineEditCellRenderer } from "../../component/TableSection/InlineEdit/patientInlineEditCells";
import { buildEnrollmentInlineEditCellRenderer } from "../../component/TableSection/InlineEdit/enrollmentInlineEditCells";

// Single source for the boilerplate every inline-edit table needs: selectors, derived options,
// commit hooks, tracking, and the shared cell renderers. `enrollment: true` opts the table into
// the enrollment-side setup (commitEnrollment + renderEnrollmentCell).
const useTableInlineEdit = ({
  t,
  view,
  patientActionCreator = ACTION.updatePatientsInPlace,
  enrollment = false,
  renderWithLink,
  // Called after a successful commit — e.g. to re-run a server-side-filtered fetch so an edited
  // row that no longer matches drops out of the list (the optimistic patch can't remove it).
  onAfterCommit
}) => {
  const assigneesAndGroups = useSelector(selectAssigneesAndUserGroups);
  const getUserOrGroupById = useSelector(selectUserOrGroupById);
  const providerFacilities = useSelector(selectProvidersFacility);
  const providerDoctors = useSelector(selectProvidersDoctors);
  const [statusesSetting] = useGetSetting(["statuses"]);
  const enrollmentStatuses = useStatuses();

  const facilitiesList = useMemo(() => Object.values(providerFacilities || {}), [providerFacilities]);

  const assigneeOptions = useMemo(
    () => (assigneesAndGroups || []).map((a) => ({ value: String(a.id), label: a.displayName })),
    [assigneesAndGroups]
  );
  const facilityOptions = useMemo(
    () => facilitiesList.map((f) => ({ value: String(f.id), label: f.name })),
    [facilitiesList]
  );
  const patientStatusOptions = useMemo(
    () => (statusesSetting || []).map((s) => ({ value: s.value, label: s.text })),
    [statusesSetting]
  );
  const physicianOptions = useMemo(
    () =>
      (providerDoctors || []).map((doc) => ({
        value: doc.id,
        label: doc.name || [doc.lastName, doc.firstName].filter(Boolean).join(", ") || String(doc.id)
      })),
    [providerDoctors]
  );

  const lookups = useMemo(
    () => ({
      assignees: assigneesAndGroups || [],
      facilities: facilitiesList,
      physicians: providerDoctors || []
    }),
    [assigneesAndGroups, facilitiesList, providerDoctors]
  );

  const { commit: rawCommitPatient } = useInlineEditPatient(lookups, {
    view,
    actionCreator: patientActionCreator
  });

  const { commit: rawCommitEnrollment } = useInlineEditEnrollment({ assignees: assigneesAndGroups || [] }, { view });

  // Wrap the commits so callers can react to a successful save (e.g. re-fetch a filtered list).
  // Read onAfterCommit through a ref so the wrapped commits stay referentially stable (the callback
  // is a fresh closure each render) and the cell renderers aren't rebuilt on every render.
  const onAfterCommitRef = useRef(onAfterCommit);
  onAfterCommitRef.current = onAfterCommit;

  const commitPatient = useCallback(
    async (...args) => {
      const result = await rawCommitPatient(...args);
      if (result?.ok && typeof onAfterCommitRef.current === "function") onAfterCommitRef.current();
      return result;
    },
    [rawCommitPatient]
  );

  const commitEnrollment = useCallback(
    async (...args) => {
      const result = await rawCommitEnrollment(...args);
      if (result?.ok && typeof onAfterCommitRef.current === "function") onAfterCommitRef.current();
      return result;
    },
    [rawCommitEnrollment]
  );

  const { trackInlineEditViewed } = useInlineEditTracking();
  const handleEditStart = useCallback(
    (columnKey) => trackInlineEditViewed({ attribute: columnKey }),
    [trackInlineEditViewed]
  );

  const renderPatientCell = useMemo(
    () =>
      buildPatientInlineEditCellRenderer({
        t,
        view,
        commitPatient,
        onEditStart: handleEditStart,
        renderWithLink,
        patientStatusOptions,
        physicianOptions
      }),
    [t, view, commitPatient, handleEditStart, renderWithLink, patientStatusOptions, physicianOptions]
  );

  const enrollmentStatusOptions = useMemo(
    () =>
      enrollment
        ? // Only selectable statuses — Expired/Exhausted are system-derived, not manually set (track-tab parity).
          Object.values(enrollmentStatuses || {})
            .filter((s) => s.isSelectable)
            .map((s) => ({ value: s.value, label: s.name }))
        : [],
    [enrollment, enrollmentStatuses]
  );

  const resolveEnrollmentStatusLabel = useCallback(
    (status) => Object.values(enrollmentStatuses || {}).find((s) => s.value === status)?.name || status,
    [enrollmentStatuses]
  );

  const renderEnrollmentCell = useMemo(
    () =>
      enrollment
        ? buildEnrollmentInlineEditCellRenderer({
            t,
            commitEnrollment,
            onEditStart: handleEditStart,
            renderWithLink,
            enrollmentStatusOptions,
            enrollmentAssigneeOptions: assigneeOptions,
            getUserOrGroupById,
            resolveStatusLabel: resolveEnrollmentStatusLabel
          })
        : null,
    [
      enrollment,
      t,
      commitEnrollment,
      handleEditStart,
      renderWithLink,
      enrollmentStatusOptions,
      assigneeOptions,
      getUserOrGroupById,
      resolveEnrollmentStatusLabel
    ]
  );

  return {
    commitPatient,
    commitEnrollment,
    handleEditStart,
    renderPatientCell,
    renderEnrollmentCell,
    lookups,
    assigneeOptions,
    facilityOptions,
    facilitiesList,
    patientStatusOptions
  };
};

export default useTableInlineEdit;
